/**
 * DataTable — shared table wrapper (SF-3733).
 *
 * A thin, opinionated wrapper over antd's Table that standardizes the loading
 * skeleton, empty state, pagination defaults, and row-click navigation used
 * across Eva list views. Generic over the row type so callers keep full column
 * typing. Later Eva pages (company/user lists) reuse this directly.
 */
import type { ReactNode } from 'react';
import { ConfigProvider, Table, theme, type TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useClickableRows } from './useClickableRows';

/** Default page size for Eva tables (server-paginated lists override as needed). */
export const DEFAULT_PAGE_SIZE = 20;

export interface DataTableProps<T> {
  /** Row data. */
  data: T[];
  /** Column definitions. */
  columns: ColumnsType<T>;
  /** Whether the underlying query is loading. */
  loading?: boolean;
  /** Stable row key extractor. */
  rowKey: (record: T, index?: number) => string;
  /**
   * Pagination config. Pass `false` to disable, or a partial config to override
   * the defaults. When omitted, client-side pagination with DEFAULT_PAGE_SIZE
   * is used.
   */
  pagination?: TablePaginationConfig | false;
  /** Optional row-click handler; clickable rows get a link-coloured name and a trailing arrow. */
  onRowClick?: (record: T) => void;
  /** Which rows open something on click (default: every row, when onRowClick is set). */
  isRowClickable?: (record: T) => boolean;
  /** Optional empty-state text. */
  emptyText?: string;
  /** Table size (default 'middle'). */
  size?: 'small' | 'middle' | 'large';
}

/** Stronger row hover background for tables whose rows open something (SF-5146). */
export function ClickableRowsHover({ children }: { children: ReactNode }) {
  const { token } = theme.useToken();
  return (
    <ConfigProvider theme={{ components: { Table: { rowHoverBg: token.colorFillTertiary } } }}>
      {children}
    </ConfigProvider>
  );
}

/** Reusable, generically-typed table for Eva list views. */
export function DataTable<T extends object>({
  data,
  columns,
  loading = false,
  rowKey,
  pagination,
  onRowClick,
  isRowClickable,
  emptyText,
  size = 'middle',
}: DataTableProps<T>) {
  const clickable = useClickableRows(columns, onRowClick, isRowClickable);
  const resolvedPagination: TablePaginationConfig | false =
    pagination === false
      ? false
      : {
          pageSize: DEFAULT_PAGE_SIZE,
          showSizeChanger: false,
          hideOnSinglePage: true,
          ...pagination,
        };

  const table = (
    <Table<T>
      dataSource={data ?? []}
      columns={clickable.columns}
      loading={loading}
      rowKey={rowKey}
      size={size}
      pagination={resolvedPagination}
      // While loading, show the spinner only: the empty text would claim there is no data yet.
      locale={emptyText ? { emptyText: loading ? <span /> : emptyText } : undefined}
      onRow={clickable.onRow}
    />
  );
  return onRowClick ? <ClickableRowsHover>{table}</ClickableRowsHover> : table;
}
